import React from 'react';

/**
 * RightsChecklist — the shared "Server Rights" control (rights-acl design §8).
 *
 * The grantable rights as a compact auto-fit checkbox grid with a master "All" toggle. ACCESS and
 * REMOTE are IMPLICIT (always-on, never shown): a granted device is always visible (ACCESS), and a
 * CLOUD device is always reached remotely (REMOTE) — so neither is a meaningful toggle here. The grid
 * only flips the GRANTABLE bits and preserves whatever implicit bits `value` carried. Pure/controlled:
 * `value` is the rights bitmask, `onChange(nextBits)` fires with the new mask.
 *
 * The bitmask values are canonical everywhere — AccessRight.java == cloudapi userRights.js. (If a local
 * dview/eview Setup ever reuses this, REMOTE is a real toggle there — reintroduce it as a row for that
 * product mode; it's hidden here because this is the cloud viewer, where remote is a given.)
 */

export const RIGHTS = { ACCESS: 1, ADMIN: 2, EXPORT: 4, REMOTE: 8, PTZ: 16, PLAYBACK: 32 };
// Always-on, never shown: ACCESS (a granted device is visible) + REMOTE (cloud is remote by definition).
export const IMPLICIT_RIGHTS = RIGHTS.ACCESS | RIGHTS.REMOTE;
// Viewer default = ACCESS|REMOTE|PLAYBACK = 41 (Java's sensible default; the seed for a new grant).
export const DEFAULT_RIGHTS = RIGHTS.ACCESS | RIGHTS.REMOTE | RIGHTS.PLAYBACK;

// The GRANTABLE rights shown as toggles (ACCESS + REMOTE are implicit — see IMPLICIT_RIGHTS). `label` =
// Java's full label; `short` = the compact form used in a grant's rights-summary line.
export const RIGHT_ROWS = [
  { bit: RIGHTS.ADMIN, label: 'Administrator', short: 'Admin', desc: 'Open Setup and administer' },
  { bit: RIGHTS.PLAYBACK, label: 'Playback', short: 'Playback', desc: 'View recorded video' },
  { bit: RIGHTS.PTZ, label: 'PTZ Control', short: 'PTZ', desc: 'Pan / tilt / zoom' },
  { bit: RIGHTS.EXPORT, label: 'Export', short: 'Export', desc: 'Export / download recordings' },
];
export const GRANTABLE = RIGHT_ROWS.reduce((m, r) => m | r.bit, 0);

/**
 * A compact human summary of a rights bitmask, for a grant's summary row.
 * "All rights" (every grantable bit) · "View only" (none) · else the short labels joined.
 */
export const rightsSummary = (bits) => {
  const b = Number(bits) || 0;
  if ((b & GRANTABLE) === GRANTABLE) return 'All rights';
  const on = RIGHT_ROWS.filter((r) => (b & r.bit) !== 0).map((r) => r.short);
  return on.length ? on.join(' · ') : 'View only';
};

export const RightsChecklist = ({ value, onChange }) => {
  const bits = Number(value) || 0;
  const allChecked = (bits & GRANTABLE) === GRANTABLE;
  const toggle = (bit) => onChange(bits ^ bit);
  const toggleAll = () => onChange(allChecked ? (bits & ~GRANTABLE) : (bits | GRANTABLE));
  // 3-column grid: "All" (select-all) as the first option, then the 5 short-labelled rights → 6 cells
  // in 2 rows, so the whole control stays light. Full names ride the title tooltip.
  return (
    <div className="setup-rights-grid">
      <label className="setup-right-cell" title="Grant every right">
        <input type="checkbox" checked={allChecked} onChange={toggleAll} />
        <span className="setup-right-cell__label">All</span>
      </label>
      {RIGHT_ROWS.map((r) => (
        <label key={r.bit} className="setup-right-cell" title={r.desc}>
          <input type="checkbox" checked={(bits & r.bit) !== 0} onChange={() => toggle(r.bit)} />
          <span className="setup-right-cell__label">{r.short}</span>
        </label>
      ))}
    </div>
  );
};

export default RightsChecklist;
